<template>
  <v-main>
    <v-container fluid>
      <!-- Header -->
      <v-row class="mt-4 ml-2 mr-2">
        <v-col cols="12">
          <v-card elevation="6" class="header-card">
            <v-card-text class="d-flex align-center py-3">
              <v-btn icon class="mr-2" @click="$router.back()">
                <v-icon>mdi-arrow-left</v-icon>
              </v-btn>
              <v-avatar color="primary" size="42" class="mr-3">
                <v-icon dark>mdi-plus</v-icon>
              </v-avatar>
              <div>
                <div class="text-h5 font-weight-bold">Create Salary Cycle</div>
                <div class="grey--text text-caption">Define a new pay period cycle</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row class="ml-2 mr-2">
        <v-col md="7">
          <v-card elevation="6" class="form-card">
            <v-card-text class="pa-6">
              <v-form ref="form" v-model="valid">
                <!-- Cycle Name -->
                <div class="section-label mb-1">Cycle Name</div>
                <v-text-field v-model="form.name" outlined dense
                  placeholder="e.g. Standard Monthly, Mid-Month Cycle"
                  :rules="[rules.required]" :error-messages="errors.name"></v-text-field>

                <!-- Day Selection -->
                <div class="section-label mb-1 mt-2">Cycle Period</div>
                <v-row>
                  <v-col md="5">
                    <v-select v-model="form.start_day" :items="days" label="Start Day" outlined dense
                      prepend-inner-icon="mdi-calendar-start" :rules="[rules.required]"
                      :error-messages="errors.start_day"></v-select>
                  </v-col>
                  <v-col md="2" class="d-flex align-center justify-center pb-6">
                    <v-icon color="grey">mdi-arrow-right</v-icon>
                  </v-col>
                  <v-col md="5">
                    <v-select v-model="form.end_day" :items="days" label="End Day" outlined dense
                      prepend-inner-icon="mdi-calendar-end" :rules="[rules.required]"
                      :error-messages="errors.end_day"></v-select>
                  </v-col>
                </v-row>

                <!-- Preview -->
                <v-expand-transition>
                  <v-alert v-if="form.start_day && form.end_day" dense outlined type="info" class="mb-4">
                    <div class="d-flex align-center">
                      <v-icon small color="info" class="mr-2">mdi-information</v-icon>
                      <span v-if="form.start_day <= form.end_day">
                        Cycle: <strong>{{ ordinal(form.start_day) }}</strong> to <strong>{{ ordinal(form.end_day) }}</strong> of the same month
                      </span>
                      <span v-else>
                        Cycle: <strong>{{ ordinal(form.start_day) }}</strong> of previous month to <strong>{{ ordinal(form.end_day) }}</strong> of current month
                      </span>
                    </div>
                  </v-alert>
                </v-expand-transition>

                <!-- Status -->
                <div class="section-label mb-1 mt-2">Status</div>
                <v-switch v-model="form.is_active" color="primary" class="mt-0"
                  :label="form.is_active ? 'Active' : 'Inactive'" hide-details></v-switch>

                <v-divider class="my-5"></v-divider>

                <!-- Actions -->
                <div class="d-flex">
                  <v-btn color="primary" depressed @click="submit" :loading="isLoading" :disabled="!valid" large>
                    <v-icon left>mdi-check</v-icon>
                    Create Salary Cycle
                  </v-btn>
                  <v-btn text class="ml-3" large to="/salary-cycles">
                    Cancel
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>

        <!-- Side Info -->
        <v-col md="5">
          <v-card elevation="2" outlined class="info-card">
            <v-card-text>
              <div class="d-flex align-center mb-3">
                <v-icon color="primary" class="mr-2">mdi-lightbulb-outline</v-icon>
                <span class="font-weight-bold">How Salary Cycles Work</span>
              </div>
              <v-divider class="mb-3"></v-divider>

              <div class="info-item mb-3">
                <div class="font-weight-medium">Same Month Cycle</div>
                <div class="grey--text text-body-2">Start Day &le; End Day (e.g. 1st to 31st)</div>
                <div class="grey--text text-body-2">Period stays within the same month.</div>
              </div>

              <div class="info-item mb-3">
                <div class="font-weight-medium">Cross Month Cycle</div>
                <div class="grey--text text-body-2">Start Day &gt; End Day (e.g. 21st to 20th)</div>
                <div class="grey--text text-body-2">Period spans from previous month to current month.</div>
              </div>

              <div class="info-item">
                <div class="font-weight-medium">Short Months</div>
                <div class="grey--text text-body-2">Days auto-adjust for months with fewer than 31 days (e.g. Feb 28/29).</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      valid: false,
      isLoading: false,
      days: Array.from({ length: 31 }, (_, i) => ({
        text: `${i + 1}`,
        value: i + 1,
      })),
      form: {
        name: "",
        start_day: "",
        end_day: "",
        is_active: true,
      },
      rules: {
        required: (value) => !!value || "Required.",
      },
    };
  },
  methods: {
    ordinal(n) {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    },
    async submit() {
      if (this.$refs.form.validate()) {
        this.isLoading = true;
        try {
          const response = await axios.post("/salary_cycles", this.form);
          if (response.data.success) {
            this.$router.push("/salary-cycles");
          }
        } catch (error) {
          console.error("Error creating salary cycle:", error);
        } finally {
          this.isLoading = false;
        }
      }
    },
  },
};
</script>

<style scoped>
.header-card {
  border-left: 4px solid #3354f4;
}
.form-card {
  border-radius: 12px;
}
.info-card {
  border-radius: 12px;
  border-color: #e0e0e0;
}
.section-label {
  font-size: 13px;
  font-weight: 600;
  color: #555;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
.info-item {
  padding-left: 12px;
  border-left: 3px solid #3354f4;
}
</style>
